/**
 * 매입 수정 모달 하단 '변경 이력' 펼침 섹션.
 *
 * SalesDetailModal 의 변경 이력 타임라인과 동일한 모양 — 펼칠 때 lazy 로
 * GET /purchases/:id/audit 호출. 분 단위 + 사용자 + action 으로 한 묶음.
 * 라벨은 공용 audit-labels.ts(getFieldLabel/getActionVerb/formatAuditValue) 사용.
 *
 * user_id 가 없는 변경(자동 트리거·시스템 적재)은 '시스템' 으로 표기.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as purchaseApi from '../lib/purchase-api';
import type { AuditEntry } from '../lib/types';
import { getFieldLabel, formatAuditValue } from '../lib/audit-labels';
import { groupAudit, type AuditGroup } from '../lib/audit-group';
import { AuditRowDataDetail } from './AuditRowDataDetail';
import { LoadingLines } from './ui/Skeleton';

function fmtDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

function label(name: string | null): string {
  return getFieldLabel('purchase', name);
}

export function PurchaseAuditSection({ purchaseId }: { purchaseId: number }) {
  const [open, setOpen] = useState(false);
  const auditQuery = useQuery({
    queryKey: ['purchaseAudit', purchaseId],
    queryFn: () => purchaseApi.getPurchaseAudit(purchaseId),
    enabled: open,
    staleTime: 30_000,
  });

  return (
    <div className="mx-6 mb-5 bg-slate-50 rounded border border-slate-200 p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-sm font-semibold text-slate-700"
      >
        <span>📜 변경 이력</span>
        <span className="text-xs text-slate-500">{open ? '▲ 접기' : '▼ 펼치기'}</span>
      </button>
      {open && (
        <div className="mt-3">
          {auditQuery.isLoading && (
            <div className="text-sm text-slate-400 text-center py-4"><LoadingLines /></div>
          )}
          {auditQuery.isError && (
            <div className="text-sm text-red-500 text-center py-4">
              오류: {(auditQuery.error as Error).message}
            </div>
          )}
          {auditQuery.data && <AuditTimeline rows={auditQuery.data} />}
        </div>
      )}
    </div>
  );
}

function AuditTimeline({ rows }: { rows: AuditEntry[] }) {
  const groups = useMemo(() => groupAudit(rows), [rows]);
  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded p-3 text-center">
        변경 이력이 없습니다.
      </div>
    );
  }
  return (
    <ul className="space-y-3 text-sm max-h-80 overflow-y-auto pr-1">
      {groups.map((g) => (
        <AuditTimelineItem key={g.key} group={g} />
      ))}
    </ul>
  );
}

function makeSummary(action: string, fields: AuditEntry[]): string {
  if (action === 'INSERT') return '매입 등록';
  if (action === 'DELETE') return '매입 삭제';
  if (fields.length === 0) return '항목 변경';
  if (fields.length === 1) return `${label(fields[0]!.fieldName)} 변경`;
  return `${label(fields[0]!.fieldName)} 외 ${fields.length - 1}개 변경`;
}

function AuditTimelineItem({ group }: { group: AuditGroup }) {
  const [expanded, setExpanded] = useState(false);
  const dotColor =
    group.action === 'INSERT'
      ? 'bg-emerald-500'
      : group.action === 'DELETE'
      ? 'bg-rose-500'
      : 'bg-blue-500';
  const who = group.userName ?? group.userId ?? '시스템';
  const fields = group.entries.filter((e) => e.fieldName);
  const summary = makeSummary(group.action, fields);
  // v2.043 — 등록(INSERT) 줄은 그 시점 행 전체가 남는다 (SalesDetailModal 과 동일)
  const rowData = group.entries.find((e) => e.rowData)?.rowData ?? null;
  const hasDetail = fields.length > 0 || rowData !== null;

  return (
    <li className="flex gap-3">
      <div className={`w-2 h-2 rounded-full ${dotColor} mt-1.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div>
          <strong>{who}</strong> {summary}{' '}
          {hasDetail && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-blue-600 hover:underline ml-1"
            >
              {expanded ? '접기' : '상세'}
            </button>
          )}
        </div>
        <div className="text-xs text-slate-500">{fmtDateTime(group.changedAt)}</div>
        {expanded && rowData !== null && (
          <AuditRowDataDetail tableName="purchase" data={rowData} />
        )}
        {expanded && fields.length > 0 && (
          <ul className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3">
            {fields.map((e) => (
              <li key={e.logId} className="text-xs">
                <span className="font-medium text-slate-600">{label(e.fieldName)}:</span>{' '}
                <span className="text-slate-400">
                  {e.oldValue === null ? '(없음)' : formatAuditValue(e.oldValue)}
                </span>{' '}
                →{' '}
                <span className="text-slate-800">
                  {e.newValue === null ? '(없음)' : formatAuditValue(e.newValue)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
